/**
 * Global, deterministic constants shared by every MailFlare runtime.
 *
 * Nothing here reads environment, time, or randomness. These are compile-time
 * invariants (Poka-Yoke): limits, allowlists, and protocol identifiers.
 */

/** Length of a canonical ULID string (10 time chars + 16 random chars). */
export const ULID_LENGTH = 26;

/** Epoch milliseconds representable in the 48-bit ULID time component. */
export const ULID_MAX_TIMESTAMP_MS = 2 ** 48 - 1;

/** Default and maximum page sizes for every list endpoint. */
export const PAGINATION = {
  defaultLimit: 25,
  maxLimit: 100,
} as const;

/**
 * Canonical request/response header names. All lowercase because `Headers` is
 * case-insensitive and lowercase avoids lookups diverging by casing.
 */
export const HEADERS = {
  requestId: "x-request-id",
  traceId: "x-trace-id",
  idempotencyKey: "idempotency-key",
  csrfToken: "x-csrf-token",
  turnstileToken: "x-turnstile-token",
  mailflareVersion: "x-mailflare-version",
} as const;

/**
 * Cookie names. The `__Host-` prefix (RFC 6265bis) forbids `Domain` and forces
 * `Path=/` + `Secure`, which prevents subdomain cookie injection.
 */
export const COOKIES = {
  session: "__Host-mf_session",
  csrf: "__Host-mf_csrf",
  webauthnChallenge: "__Host-mf_webauthn",
  oidcState: "__Host-mf_oidc",
} as const;

/** System folders are the mutually exclusive coarse location of a message. */
export const SYSTEM_FOLDERS = ["inbox", "sent", "drafts", "trash", "spam", "archive"] as const;
export type SystemFolder = (typeof SYSTEM_FOLDERS)[number];

/** Folders that hold user-authored-but-unsent content; not message rows. */
export const DRAFT_FOLDER: SystemFolder = "drafts";

/** Folders a normal `list messages` call may address. */
export const VISIBLE_FOLDERS: readonly SystemFolder[] = [
  "inbox",
  "sent",
  "trash",
  "spam",
  "archive",
] as const;

/** Message sort fields; every one is backed by an index. */
export const MESSAGE_SORT_FIELDS = ["received_at", "sent_at", "subject"] as const;
export type MessageSortField = (typeof MESSAGE_SORT_FIELDS)[number];

export const SORT_ORDERS = ["asc", "desc"] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

/**
 * Attachment MIME allowlist. Anything not present is stored but NEVER rendered
 * inline and is always served with `Content-Disposition: attachment`.
 */
export const ATTACHMENT_MIME_ALLOWLIST = [
  "application/pdf",
  "application/json",
  "application/zip",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "text/plain",
  "text/csv",
  "text/calendar",
  "message/rfc822",
] as const;
export type AllowedAttachmentMime = (typeof ATTACHMENT_MIME_ALLOWLIST)[number];

/**
 * MIME types that may be rendered inline in the reading pane. A strict subset of
 * {@link ATTACHMENT_MIME_ALLOWLIST}. Everything else is force-downloaded.
 */
export const INLINE_MIME_ALLOWLIST = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
] as const;

/** Deterministic caps. Exceeding any of these is a `ValidationError`. */
export const LIMITS = {
  /** Inbound size ceiling enforced by Email Routing. */
  maxEmailBytes: 25 * 1024 * 1024,
  maxAttachmentBytes: 25 * 1024 * 1024,
  maxAttachmentsPerMessage: 50,
  maxTotalAttachmentBytes: 25 * 1024 * 1024,
  maxRecipientsPerMessage: 100,
  maxSubjectLength: 998,
  maxDisplayNameLength: 254,
  maxAddressLength: 320,
  maxLocalPartLength: 64,
  maxDomainLength: 253,
  /** Partial index body cap for FTS (bytes of extracted text). */
  maxIndexedBodyBytes: 1024 * 1024,
  maxHtmlBodyBytes: 5 * 1024 * 1024,
  maxTextBodyBytes: 2 * 1024 * 1024,
  maxSearchQueryLength: 256,
  maxLabelNameLength: 64,
  maxSnippetLength: 240,
  maxAuditDetailBytes: 32 * 1024,
} as const;

/** Spam scoring thresholds on a 0..10 scale. */
export const SPAM_THRESHOLDS = {
  suspect: 3,
  spam: 5,
  phishing: 8,
} as const;

/** Rate limits, evaluated by the `RateLimiter` Durable Object. */
export const RATE_LIMITS = {
  authPerIp: { limit: 20, windowSeconds: 300 },
  signupPerIp: { limit: 5, windowSeconds: 3600 },
  apiPerIp: { limit: 300, windowSeconds: 60 },
  apiPerUser: { limit: 600, windowSeconds: 60 },
  searchPerUser: { limit: 120, windowSeconds: 60 },
  uploadPerUser: { limit: 60, windowSeconds: 3600 },
  sendPerUser: { limit: 500, windowSeconds: 3600 },
  sendPerMailbox: { limit: 200, windowSeconds: 3600 },
  exportPerUser: { limit: 3, windowSeconds: 86400 },
} as const;
export type RateLimitName = keyof typeof RATE_LIMITS;

/** Queue names. One concern per queue; each has a DLQ. */
export const QUEUES = {
  inbound: "mailflare-inbound",
  index: "mailflare-index",
  send: "mailflare-send",
  notify: "mailflare-notify",
  dlq: "mailflare-dlq",
} as const;
export type QueueName = (typeof QUEUES)[keyof typeof QUEUES];

/** R2 key prefixes. Keys are `prefix/...` and always derive from a ULID. */
export const R2_PREFIX = {
  rawEmail: "raw",
  attachment: "attachments",
  export: "exports",
  backup: "backups",
} as const;

/** Durable Object instance sharding for mailbox rooms. */
export const MAILBOX_ROOM_SHARDS = 16;

/** WebSocket broadcast event types. */
export const MAILBOX_EVENT_TYPES = [
  "message.created",
  "message.updated",
  "message.flags_changed",
  "message.deleted",
  "label.changed",
  "draft.changed",
  "sync.required",
] as const;
export type MailboxEventType = (typeof MAILBOX_EVENT_TYPES)[number];

/** User-facing roles, ordered least to most privileged. */
export const USER_ROLES = ["member", "admin", "owner"] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Audit log actions. Append-only; every privileged mutation records one. */
export const AUDIT_ACTIONS = [
  "auth.login",
  "auth.logout",
  "auth.passkey_registered",
  "auth.passkey_removed",
  "auth.totp_enabled",
  "auth.totp_disabled",
  "auth.failed_login",
  "user.created",
  "user.updated",
  "user.suspended",
  "user.role_changed",
  "user.deleted",
  "domain.created",
  "domain.verified",
  "domain.deleted",
  "mailbox.created",
  "mailbox.updated",
  "mailbox.deleted",
  "alias.created",
  "alias.deleted",
  "message.deleted",
  "message.exported",
  "draft.deleted",
  "routing_rule.created",
  "routing_rule.updated",
  "routing_rule.deleted",
  "quota.updated",
  "data.export_requested",
  "data.deletion_requested",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Delivery outcomes recorded on outbound messages. */
export const DELIVERY_STATUSES = [
  "queued",
  "sending",
  "sent",
  "failed",
  "bounced",
  "complained",
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Bounce classification. */
export const BOUNCE_TYPES = ["hard", "soft", "complaint"] as const;
export type BounceType = (typeof BOUNCE_TYPES)[number];

/** DMARC evaluation results as surfaced by the MTA. */
export const DMARC_RESULTS = ["pass", "fail", "none", "temperror", "permerror"] as const;
export type DmarcResult = (typeof DMARC_RESULTS)[number];

/** Spam verdicts attached to an inbound message. */
export const SPAM_VERDICTS = ["ham", "suspect", "spam", "phishing"] as const;
export type SpamVerdict = (typeof SPAM_VERDICTS)[number];

/** Alias kinds. A `catch_all` alias matches any unclaimed local part. */
export const ALIAS_KINDS = ["alias", "catch_all"] as const;
export type AliasKind = (typeof ALIAS_KINDS)[number];

/** Routing rule actions applied during inbound fan-out. */
export const ROUTING_ACTIONS = ["route", "forward", "discard", "reject"] as const;
export type RoutingAction = (typeof ROUTING_ACTIONS)[number];

/** Contact / message address roles. */
export const ADDRESS_KINDS = ["from", "to", "cc", "bcc", "reply_to", "sender"] as const;
export type AddressKind = (typeof ADDRESS_KINDS)[number];

/** Prefix for R2 idempotency / dedupe markers. */
export const SEARCH_PROVIDER_KINDS = ["d1_fts5", "vectorize", "meilisearch", "typesense"] as const;
export type SearchProviderKind = (typeof SEARCH_PROVIDER_KINDS)[number];

/** Semantic version of the API contract, surfaced in `x-mailflare-version`. */
export const API_VERSION = "2026-09-01";
